import React, { useState, useEffect } from "react";
import { getDoc, getFirestore, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from '../../../credenciales';
import { Container, Alert, Button, Table, Spinner } from 'react-bootstrap';
import { Html5Qrcode } from 'html5-qrcode';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

const QrPage = () => {
  const db = getFirestore(app);
  const auth = getAuth(app);
  const { idActividad } = useParams();

  const [activityData, setActivityData] = useState(null);
  const [qrResult, setQrResult] = useState(null);
  const [todayFechaIndex, setTodayFechaIndex] = useState(-1);
  const [usuariosDeHoy, setUsuariosDeHoy] = useState([]);
  const [scanner, setScanner] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [scanning, setScanning] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchActivityData = async () => {
      const actividadRef = doc(db, "actividades", idActividad);
      const actividadSnap = await getDoc(actividadRef);

      if (!actividadSnap.exists()) {
        setErrorMessage("Actividad no encontrada.");
        return;
      }

      const actividad = actividadSnap.data();
      setActivityData(actividad);

      const indexHoy = actividad.fechas?.findIndex(fecha => fecha.fecha === today);
      setTodayFechaIndex(indexHoy);

      if (indexHoy === -1) {
        setErrorMessage("No hay actividad para hoy.");
        return;
      }

      const hoy = actividad.fechas[indexHoy];
      const usuarios = [
        ...(hoy.profesores?.map(p => ({ ...p, tipo: "profesor" })) || []),
        ...(hoy.usuarios?.map(u => ({ ...u, tipo: "alumno" })) || [])
      ];
      setUsuariosDeHoy(usuarios);
    };

    fetchActivityData();
  }, [idActividad]);

  const handleScan = async (data) => {
    if (!data) return;
    
    setScanning(true);

    const userId = data.trim();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setScanning(false);
      Swal.fire({
        title: "No se pudo identificar al profesor.",
        icon: "error"
      });
      //alert("Error: no se pudo identificar al profesor.");
      return;
    }

    const profesorId = currentUser.email;
    setQrResult(userId);

    const actividadRef = doc(db, "actividades", idActividad);
    const actividadSnap = await getDoc(actividadRef);

    if (!actividadSnap.exists()) {
      setScanning(false);
      //alert("Actividad no encontrada.");
      Swal.fire({
        title: "Actividad no encontrada.",
        icon: "error"
      });
      return;
    }

    const actividadData = actividadSnap.data();
    const fechas = actividadData.fechas || [];

    const indexHoy = fechas.findIndex(f => f.fecha === today);
    if (indexHoy === -1) {
      setScanning(false);
      //alert("No hay actividad para hoy.");
      Swal.fire({
        title: "No hay actividad para hoy.",
        icon: "error"
      });
      return;
    }

    const fechaHoy = fechas[indexHoy];

    const userRef = doc(db, "usuarios", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      setScanning(false);
      Swal.fire({
        title: "Usuario no encontrado.",
        icon: "error"
      });
      return;
    }

    const userData = userSnap.data();

    const isAlumno = fechaHoy.usuarios?.some(u => u.id === userId);
    const isProfesorEscaneado = fechaHoy.profesores?.some(p => p.id === userId);

    if (!isAlumno && !isProfesorEscaneado) {
      setScanning(false);
      //alert("Este usuario no está registrado en la actividad de hoy.");
      Swal.fire({
        title: "Este usuario no está registrado en la actividad de hoy.",
        icon: "error"
      });
      return;
    }

    if (isAlumno && !userData.estado) {
      //alert("Este alumno no puede dar el presente (estado inactivo).");
      setScanning(false);
      Swal.fire({
        title: "Este alumno no puede dar el presente (estado inactivo).",
        icon: "error"
      });
      return;
    }

    const yaPresente = fechaHoy.usuarios?.some(u => u.id === userId && u.presente);
    const profesorYaPresente = fechaHoy.profesores?.some(p => p.id === profesorId && p.presente);

    if (yaPresente) {
      setScanning(false);
      Swal.fire({
        title: "Este alumno ya fue marcado como presente.",
        icon: "info"
      });
      return;
    }

    const updatedFechas = fechas.map((fecha, index) => {
      if (index !== indexHoy) return fecha;

      const updatedUsuarios = fecha.usuarios?.map(u => {
        if (u.id === userId && !u.presente) {
          return { ...u, presente: true };
        }
        return u;
      });

      const updatedProfesores = fecha.profesores?.map(p => {
        if (p.id === profesorId && !p.presente) {
          return { ...p, presente: true };
        }
        return p;
      });

      return {
        ...fecha,
        usuarios: updatedUsuarios,
        profesores: updatedProfesores
      };
    });

    await updateDoc(actividadRef, { fechas: updatedFechas });

    const nuevaFecha = updatedFechas[indexHoy];
    const usuariosActualizados = [
      ...(nuevaFecha.profesores?.map(p => ({ ...p, tipo: "profesor" })) || []),
      ...(nuevaFecha.usuarios?.map(u => ({ ...u, tipo: "alumno" })) || [])
    ];
    setUsuariosDeHoy(usuariosActualizados);

    setScanning(false);
    Swal.fire({
      title: "Alumno marcado como presente.",
      icon: "success"
    });

  };

  const handleStartScan = () => {
    const qrScanner = new Html5Qrcode("reader");

    qrScanner.start(
      { facingMode: "environment" },
      {
        fps: 30, // más bajo para estabilidad
        qrbox: { width: 300, height: 300 },
        aspectRatio: 1.3333, // 4:3, más compatible
        disableFlip: true,
        experimentalFeatures: {
          useBarCodeDetectorIfSupported: true // Usa API nativa si está disponible
        }
      },
      (decodedText) => {
        qrScanner.stop();
        handleScan(decodedText); // tu función
      },
      (errorMessage) => {
        // Omitimos errores para no saturar la consola
      }
    ).then(() => {
      setScanner(qrScanner);
    }).catch(err => {
      console.error("Error iniciando escáner:", err);
    });
    return () => {
      qrScanner.stop();
      qrScanner.clear();
    };
  };

  return (
    <Container>
      {scanning ? <div style={{justifyContent: "center", display: "flex", height:"100vh", alignItems:"center"}}><Spinner animation="grow"/></div> :
      <>
      <h1>Actividad: {activityData?.nombre}</h1>

      <Button onClick={handleStartScan} disabled={todayFechaIndex === -1}>Escanear</Button>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <div id="reader" style={{ marginTop: 20 }}></div>

      {qrResult && <p>Último escaneado: <strong>{qrResult}</strong></p>}

      <h3>Asistencia de hoy ({today}):</h3>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Usuario</th>
            <th>Presente</th>
          </tr>
        </thead>
        <tbody>
          {usuariosDeHoy.length > 0 ? (
            usuariosDeHoy.map((u, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{u.id} {u.tipo === "profesor" && <span className="text-primary"> - Profesor</span>}</td>
                <td>{u.presente ? "✅" : "❌"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3">No hay usuarios registrados para hoy.</td>
            </tr>
          )}
        </tbody>
      </Table>
      </>
    }
    </Container>
  );
};

export default QrPage;
